import { Fragment, useState } from 'react';

import { Popover, Transition } from '@headlessui/react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useAtom } from 'jotai';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { IconType } from 'react-icons';
import { BsEyeFill } from 'react-icons/bs';
import {
  HiOutlineArrowNarrowLeft,
  HiOutlineTrash,
  HiPencilAlt,
  HiPlusCircle,
} from 'react-icons/hi';

import { deleteAd } from '@/api/fetch/marketplace-api';
import { rudderstackTrackEvent } from '@/api/rudderstack/client';
import { AuthTokenAtom } from '@/modules/core/store/grpc-free-store';
import { IListingItem, Itransaction } from '@/modules/core/types';
import { genItemLink } from '@/modules/core/utils/utils';
import Button from '@/tayara-kit/Button';
import Text from '@/tayara-kit/Text';

dayjs.extend(relativeTime);

interface TablesProps {
  data: IListingItem[];
  leadsData: any;
  viewsData: any;
  transactionList: Itransaction[];
}

export interface IAction {
  label: string;
  icon: IconType;
  action: () => void;
  classes?: string;
  message?: string;
  shortLabel?: string;
  requiresConfirmation?: boolean;
}

const Tables: React.FC<TablesProps> = ({ data }) => {
  const [authToken] = useAtom(AuthTokenAtom);

  const [, setMarkAsDeleted] = useState<boolean>(false);

  const formattedTitle = (title: string) => title.replace(/ /g, '_');
  const { push } = useRouter();

  const handleDeleteAd = (id: string) => {
    deleteAd(authToken, id).then(() => {
      setMarkAsDeleted(true);
    });
  };

  const actions = [
    {
      label: 'Supprimer',
      icon: HiOutlineTrash,
      classes:
        'flex self-end btn-xs text-gray-500 bg-transparent hover:bg-transparent hover:text-red-500',
      requiresConfirmation: true,
      shortLabel: 'Supprimer',
      message:
        "Êtes-vous sûr de Supprimer l'annonce ? cette action est irréversible.",
    },
  ];

  return (
    <div className="mt-8">
      <div className="flex flex-wrap justify-end lg:justify-between items-end">
        <div className="sm:flex-auto">
          <h1 className="text-lg font-extrabold text-neutral-500">
            Mes Annonces
          </h1>
          <p className=" text-neutral-500 font-light  text-sm"></p>
          <Text
            className=" justify-self-center mr-auto w-fit text-neutral-500 font-light text-sm mt-2"
            text="Une liste complète de vos annonces et de leurs statistiques sur les 30 derniers jours, y compris les action les plus importantes."
          />
        </div>
        <div className="w-full justify-end flex items-center gap-2 mt-4">
          {/* <button
            className="flex items-start px-1 py-[2.9px] rounded-lg !border-[1px] !border-info  shadow-sm  hover:text-white focus:outline-none focus:ring-2 focus:ring-info focus:ring-offset-2 sm:w-auto text-sm   font-bold  font-arabic"
            onClick={() =>
              rudderstackTrackEvent('Clicked on listing insertion button')
            }
          >
            <DownloadReceipts transactionList={transactionList} />
          </button> */}
          <a
            className="self-end sm:self-auto"
            href="https://www.tayara.tn/profile/?t=myListings"
            target={'_blank'}
          >
            <Button
              aria-label="Voir mes transactions"
              className={`
                  
            self-end btn-sm text-info hover:bg-transparent hover:text-blue-700 !pl-0 md:pl-0 gap-x-4`}
              icon={<BsEyeFill size={24} />}
            >
              Voir mes annonces
            </Button>
          </a>
          <Link
            passHref
            href={'/post-listing'}
            aria-label="Ajouter une annonce"
          >
            <button
              className="flex truncate items-start gap-x-2  py-2 px-4 rounded-lg !border-[1px] !border-primary text-primary shadow-sm hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:w-auto text-sm   font-bold  font-arabic"
              onClick={() =>
                rudderstackTrackEvent('Clicked on listing insertion button')
              }
            >
              <HiPlusCircle size={22} />
              Ajouter une annonce
            </button>
          </Link>
        </div>
      </div>
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="relative pb-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full table-fixed divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="min-w-[6rem] py-3.5 px-3 text-left text-sm font-semibold text-gray-900"
                    >
                      Aperçu
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Titre
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Date
                    </th>
                    {/* <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Prospects
                    </th> */}
                    {/* <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Clics
                    </th> */}
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {data?.map((classified) => (
                    <tr key={classified.id} className=" hover:bg-neutral-100">
                      <td className="relative w-fit px-6 sm:pl-3">
                        {/* <input
                          type="checkbox"
                          className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"
                          value={classified.title}
                          checked={selectedPeople.includes(classified)}
                          onChange={(e) =>
                            setSelectedPeople(
                              e.target.checked
                                ? [...selectedPeople, classified]
                                : selectedPeople.filter((p) => p !== classified)
                            )
                          }
                        /> */}
                        <div className="mask mask-square rounded-md w-10 h-10 overflow-hidden">
                          <img
                            className="h-full w-full object-cover"
                            src={
                              classified?.images[0]?.includes('http')
                                ? classified?.images[0]
                                : `https://www.tayara.tn/mediaGateway/resize-image?img=${classified?.images[0]}&w=300`
                            }
                            alt={`tayara image for classified: ${classified.title}`}
                          />
                        </div>
                      </td>
                      <td>
                        <span className="block font-bold truncate w-56">
                          {classified.title}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <span className="block truncate w-56">
                          {classified.description}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {dayjs(classified.metadata?.publishedOn).fromNow()}
                      </td>

                      {/* <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {leadsData[index]?.value}
                      </td>

                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {viewsData[index]?.value}
                      </td> */}

                      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <div className="flex items-center">
                          <Button
                            aria-label="Voir mes messages"
                            className={`
                  
            self-end btn-xs text-gray-500 bg-transparent hover:bg-transparent hover:text-blue-700`}
                            icon={<BsEyeFill size={24} />}
                            onClick={() =>
                              window.open(genItemLink(classified), '_blank')
                            }
                          ></Button>
                          <Button
                            aria-label="Voir mes messages"
                            className={`
                  
            self-end btn-xs text-gray-500 bg-transparent hover:bg-transparent hover:text-blue-700`}
                            icon={<HiPencilAlt size={24} />}
                            onClick={() =>
                              window.open(`/edit-ad/${classified.id}`, '_blank')
                            }
                          >
                            Editer
                          </Button>

                          <div className="">
                            {actions.map((item, i) =>
                              item.requiresConfirmation ? (
                                <Popover
                                  key={item.label}
                                  className="relative w-fit"
                                >
                                  {({ close }) => (
                                    <>
                                      <Popover.Button
                                        as={Button}
                                        aria-label={item.label}
                                        className={`${item.classes}`}
                                        icon={<item.icon size={22} />}
                                      >
                                        <span className="hidden md:hidden xl:block">
                                          {item.label}
                                        </span>
                                      </Popover.Button>
                                      <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-200"
                                        enterFrom="opacity-0 translate-y-1"
                                        enterTo="opacity-100 translate-y-0"
                                        leave="transition ease-in duration-150"
                                        leaveFrom="opacity-100 translate-y-0"
                                        leaveTo="opacity-0 translate-y-1"
                                      >
                                        <Popover.Panel
                                          as="div"
                                          className="absolute -left-48 -top-[4rem] w-[18rem] rounded-2xl bg-white p-2 shadow-2xl z-50"
                                        >
                                          <div className="flex flex-col">
                                            <p className="self-start text-start whitespace-pre-line mb-2 p-2 text-xs text-gray-700">
                                              {item.message || 'Continuer'}
                                            </p>
                                            {/* Actions */}
                                            <div className="flex flex-1 gap-2">
                                              <Button
                                                className="basis-full shrink bg-transparent  text-neutral-600 font-normal "
                                                onClick={() => close()}
                                              >
                                                <HiOutlineArrowNarrowLeft className="mr-2" />
                                                Annuler
                                              </Button>
                                              <Button
                                                className="basis-full shrink bg-transparent  text-red-500 font-normal"
                                                icon={<item.icon size={17} />}
                                                onClick={() => {
                                                  handleDeleteAd(classified.id);
                                                  close();
                                                  push(
                                                    `/item/${formattedTitle(
                                                      classified.title
                                                    )}_${classified.id}/`
                                                  );
                                                }}
                                              >
                                                Confirmer
                                              </Button>
                                            </div>
                                          </div>
                                        </Popover.Panel>
                                      </Transition>
                                    </>
                                  )}
                                </Popover>
                              ) : (
                                // eslint-disable-next-line react/jsx-key
                                <Button
                                  key={i}
                                  onClick={() => handleDeleteAd(classified.id)}
                                  aria-label={item.label}
                                  className={`${item.classes}
               btn-xs normal-case font-bold rounded-lg bg-transparent hover:bg-slate-100 text-blue-500  border-none !w-fit !px-2`}
                                  icon={
                                    <item.icon
                                      size={18}
                                      className="text-blue-500"
                                    />
                                  }
                                >
                                  <span className="hidden md:hidden xl:block text-xs">
                                    {item.label}
                                  </span>
                                </Button>
                              )
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tables;
